import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RangeSliderOption } from '@immosky/types';
import { clamp, remap } from '../../utils/math';
import {
  connectedPositionsBottomLeftPriority,
  connectedPositionsBottomRightPriority,
} from '../../utils/dropdown-conntected-positions';
import { relativeCoordinate } from '../../utils/coordinates';
import { RangeSliderKnobComponent } from './range-slider-knob/range-slider-knob.component';

@Component({
  selector: 'lib-range-slider',
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeSliderComponent implements AfterViewChecked {
  @Input()
  options!: RangeSliderOption[];

  @Input()
  minOption!: RangeSliderOption;

  @Input()
  maxOption!: RangeSliderOption;

  @Output()
  minChange = new EventEmitter<RangeSliderOption>();

  @Output()
  maxChange = new EventEmitter<RangeSliderOption>();

  @ViewChild('slider')
  sliderRef!: ElementRef<HTMLElement>;

  @ViewChild('trackHighlight')
  trackHighlight!: ElementRef<HTMLElement>;

  @ViewChild('leftKnob')
  leftKnob!: RangeSliderKnobComponent;

  @ViewChild('rightKnob')
  rightKnob!: RangeSliderKnobComponent;

  @ContentChild(TemplateRef)
  unitTemplate!: TemplateRef<unknown>;

  sliderLeft = 0;
  sliderRight = 0;
  sliderWidth = 0;

  leftDropdownOverlayPositions = connectedPositionsBottomLeftPriority;
  rightDropdownOverlayPositions = connectedPositionsBottomRightPriority;

  get defaultMinOption() {
    return this.options[0];
  }

  get defaultMaxOption() {
    return this.options[this.options.length - 1];
  }

  get minValueIndex() {
    return this.getIndexForOption(this.minOption);
  }

  get maxValueIndex() {
    return this.getIndexForOption(this.maxOption);
  }

  get minIndex(): number {
    return 0;
  }

  get maxIndex(): number {
    return this.options.length - 1;
  }

  get minOptionIndex(): number {
    return this.options.findIndex((option: RangeSliderOption) => {
      return this.minOption.value === option.value;
    });
  }

  get maxOptionIndex(): number {
    return this.options.findIndex((option: RangeSliderOption) => {
      return this.maxOption.value === option.value;
    });
  }

  get trackHighlightWidth(): string {
    const width = remap(
      this.minIndex,
      this.maxIndex,
      this.indexDistance,
      0,
      this.sliderWidth
    );
    return `${width}px`;
  }

  get indexDistance(): number {
    return this.maxOptionIndex - this.minOptionIndex;
  }

  get trackHighlightOffset(): string {
    const xOffset = remap(
      this.minIndex,
      this.maxIndex,
      this.minOptionIndex,
      0,
      this.sliderWidth
    );
    return `translateX(${xOffset}px)`;
  }

  constructor(private cdRef: ChangeDetectorRef) {}

  ngAfterViewChecked() {
    window.requestAnimationFrame(() => {
      const clientRect = this.sliderRef.nativeElement.getBoundingClientRect();
      this.sliderLeft = clientRect.left;
      this.sliderRight = clientRect.right;
      this.sliderWidth = this.sliderRef.nativeElement.clientWidth;
      this.cdRef.detectChanges();
    });
  }

  updateLocalMin(option: RangeSliderOption) {
    this.minOption = this.getSafeMinOption(option);
    this.cdRef.markForCheck();
  }

  selectMin(option: RangeSliderOption) {
    this.minChange.emit(this.getSafeMinOption(option));
  }

  private getSafeMinOption(option: RangeSliderOption): RangeSliderOption {
    if (this.getIndexForOption(option) < this.maxValueIndex) {
      return option;
    } else {
      return this.options[this.maxValueIndex - 1];
    }
  }

  updateLocalMax(option: RangeSliderOption) {
    this.maxOption = this.getSafeMaxOption(option);
    this.cdRef.markForCheck();
  }

  selectMax(option: RangeSliderOption) {
    this.maxChange.emit(this.getSafeMaxOption(option));
  }

  private getSafeMaxOption(option: RangeSliderOption): RangeSliderOption {
    if (this.getIndexForOption(option) > this.minValueIndex) {
      return option;
    } else {
      return this.options[this.minValueIndex + 1];
    }
  }

  private getIndexForOption(option: RangeSliderOption) {
    return this.options.findIndex((o: RangeSliderOption) => {
      return o.value === option?.value;
    });
  }

  trackBy(index: number, option: RangeSliderOption) {
    return option.value;
  }

  onSliderClick(event: MouseEvent | TouchEvent) {
    const coordinate = relativeCoordinate(this.sliderRef.nativeElement, event);
    const x = clamp(0, coordinate.x, this.sliderWidth);

    const distanceToLeftKnob = this.getDistanceToKnob(x, this.leftKnob);
    const distanceToRightKnob = this.getDistanceToKnob(x, this.rightKnob);

    if (distanceToLeftKnob < distanceToRightKnob) {
      // left is closer
      this.selectMin(this.findClosestOption(x));
    } else {
      // right is closer
      this.selectMax(this.findClosestOption(x));
    }
  }

  private getDistanceToKnob(x: number, knob: RangeSliderKnobComponent): number {
    const knobCenterX = knob.x + knob.knobWidth * 0.5;
    return Math.abs(x - knobCenterX);
  }

  private findClosestOption(x: number): RangeSliderOption {
    const closestIndex = Math.round(
      remap(0, this.sliderWidth, x, 0, this.maxIndex)
    );
    const safeIndex = clamp(0, closestIndex, this.maxIndex);
    return this.options[safeIndex];
  }
}
